import type { TipoSolicitacao } from './solicitacao.types';

/**
 * Modelos das mensagens de contato com o colaborador (botões "Chamar no WhatsApp/Teams" da
 * Movimentação). O SI edita o texto em Configurações → Mensagens de contato (si_config_mensagens);
 * sem edição, vale o texto padrão daqui. Mesmo texto no WhatsApp e no Teams (decisão).
 */
export type IdModeloMensagem =
  | 'envio_codigo'
  | 'devolucao_codigo'
  | 'admissao'
  | 'desligamento'
  | 'desligamento_presencial'
  | 'envio'
  | 'devolucao';

export interface ModeloMensagemPadrao {
  id: IdModeloMensagem;
  titulo: string;
  /** Quando o sistema usa este modelo (aparece na tela de configuração). */
  quando: string;
  texto: string;
}

/** Variáveis que o SI pode usar no texto. `{documentos}` = links dos documentos escolhidos;
 * `{endereco}` = do formulário (Admissão) ou do cadastro. Negrito do WhatsApp: *assim*. */
export const VARIAVEIS_MENSAGEM: ReadonlyArray<{ chave: string; label: string }> = [
  { chave: '{primeiro_nome}', label: 'primeiro nome' },
  { chave: '{nome}', label: 'nome' },
  { chave: '{remetente}', label: 'quem envia' },
  { chave: '{codigo_rastreio}', label: 'código de rastreio' },
  { chave: '{data_prevista}', label: 'data prevista' },
  { chave: '{endereco}', label: 'endereço' },
  { chave: '{documentos}', label: 'documentos' },
];

/** Os textos que o SI já usava (antes fixos no código), com variáveis no lugar dos dados. */
export const MODELOS_MENSAGEM_PADRAO: readonly ModeloMensagemPadrao[] = [
  {
    id: 'envio_codigo',
    titulo: 'Envio com código de rastreio',
    quando: 'Qualquer tipo · envio · com código registrado',
    texto: [
      'Equipamento postado.',
      'Acompanhamento através do site dos correios: https://rastreamento.correios.com.br/',
      'com o Código de Rastreamento: {codigo_rastreio}',
      'Fica de olho no rastreamento e *ME AVISA ASSIM QUE RECEBER*, por favor.',
      '',
      'Segue também um informativo para ler e se atentar antes do equipamento chegar na sua casa.',
      '{documentos}',
    ].join('\n'),
  },
  {
    id: 'devolucao_codigo',
    titulo: 'Devolução com código de postagem',
    quando: 'Qualquer tipo · devolução · com código registrado',
    texto: [
      'Código de Postagem/Rastreamento: {codigo_rastreio}',
      '',
      '•⁠ ⁠Para utilizá-lo, você deve se dirigir a uma Agência Própria ou Franqueada dos Correios, levando consigo, obrigatoriamente, o código e o objeto para postagem.',
      '•⁠ ⁠Lembrando que não precisa ser exatamente você a entregar na agência, pode ser alguém da sua confiança.',
      '•⁠ ⁠Para maiores informações sobre embalagem e acondicionamento, leia o informativo.',
      '{documentos}',
      '',
      '•⁠ ⁠*Me informe assim que realizar a postagem*, por favor.',
    ].join('\n'),
  },
  {
    id: 'admissao',
    titulo: 'Admissão (boas-vindas)',
    quando: 'Admissão · sem código · endereço do formulário de admissão',
    texto: [
      'Oi {primeiro_nome}, tudo bem?',
      'Eu sou {remetente}, do Time de SI da RARO. Estou entrando em contato para avisar que recebi aqui a informação do seu início, com previsão para o dia {data_prevista}.',
      'Estou preparando um equipamento para as suas atividades na empresa.',
      'Pode me confirmar se seu endereço para a entrega está correto?',
      '{endereco}',
    ].join('\n'),
  },
  {
    id: 'desligamento',
    titulo: 'Desligamento',
    quando: 'Desligamento · sem código · endereço do cadastro',
    texto: [
      'Ei {primeiro_nome}.',
      'Recebi aqui a notificação do seu desligamento. 😢',
      '',
      'Estou entrando em contato para combinarmos a devolução dos equipamentos.',
      '',
      'Poderia:',
      '•⁠ ⁠me enviar algumas fotos do(s) equipamento(s)? (seguir modelo anexo)',
      '•⁠ ⁠me confirmar se seu endereço está correto?',
      '{endereco}',
    ].join('\n'),
  },
  {
    id: 'desligamento_presencial',
    titulo: 'Desligamento (BH e região)',
    quando: 'Desligamento · sem código · cidade com atendimento presencial (não pede endereço)',
    texto: [
      'Ei {primeiro_nome}.',
      'Recebi aqui a notificação do seu desligamento. 😢',
      '',
      'Estou entrando em contato para combinarmos a devolução dos equipamentos.',
      '',
      'Poderia:',
      '•⁠ ⁠me enviar algumas fotos do(s) equipamento(s)? (seguir modelo anexo)',
    ].join('\n'),
  },
  {
    id: 'envio',
    titulo: 'Envio (demais tipos)',
    quando: 'Troca, nova alocação, periféricos… · envio · sem código · endereço do cadastro',
    texto: [
      'Olá, {primeiro_nome}! Estou entrando em contato para combinarmos o envio do seu equipamento.',
      '',
      'Pode me confirmar se seu endereço para a entrega está correto?',
      '{endereco}',
    ].join('\n'),
  },
  {
    id: 'devolucao',
    titulo: 'Devolução (demais tipos)',
    quando: 'Troca, devolução… · devolução · sem código · endereço do cadastro',
    texto: [
      'Olá, {primeiro_nome}! Estou entrando em contato para combinarmos a devolução do equipamento.',
      '',
      'Poderia:',
      '•⁠ ⁠me enviar algumas fotos do estado atual do equipamento? (seguir modelo anexo)',
      '•⁠ ⁠me confirmar se seu endereço está correto?',
      '{endereco}',
    ].join('\n'),
  },
];

export interface SituacaoMensagem {
  tipoSolicitacao: TipoSolicitacao;
  tipoFluxo: 'envio' | 'devolucao';
  codigoRastreio: string | null;
  /** Desligamento sem código em BH/região: não pede endereço. */
  atendimentoPresencial?: boolean;
}

/** Qual modelo vale pra esta Movimentação (mesma regra de antes, agora com modelos editáveis). */
export function escolherModeloMensagem(s: SituacaoMensagem): IdModeloMensagem {
  if (s.codigoRastreio) return s.tipoFluxo === 'devolucao' ? 'devolucao_codigo' : 'envio_codigo';
  if (s.tipoSolicitacao === 'desligamento') return s.atendimentoPresencial ? 'desligamento_presencial' : 'desligamento';
  if (s.tipoSolicitacao === 'admissao') return 'admissao';
  return s.tipoFluxo === 'devolucao' ? 'devolucao' : 'envio';
}

export interface ValoresMensagem {
  nome: string;
  remetente: string;
  codigoRastreio: string | null;
  dataPrevista: string | null;
  /** Endereço já formatado; sem endereço, a mensagem pede o completo. */
  endereco?: string | null;
}

/** No lugar de {endereco} quando não há endereço no formulário/cadastro. */
export const SEM_ENDERECO =
  '(não encontrei seu endereço aqui — pode me enviar completo? rua, número, complemento, bairro, cidade, UF e CEP)';

export interface DocumentoNaMensagem {
  titulo: string;
  url: string;
}

/**
 * Preenche as variáveis. Documentos entram onde estiver `{documentos}` (como "📎 Título: link");
 * sem a variável no texto, vão no fim. Linha que só tinha `{documentos}` some se não houver
 * documento (pra não sobrar linha em branco).
 */
export function preencherMensagem(texto: string, valores: ValoresMensagem, documentos: DocumentoNaMensagem[]): string {
  const primeiroNome = valores.nome.trim().split(/\s+/)[0] || valores.nome;
  const blocoDocumentos = documentos.map((d) => `📎 ${d.titulo}: ${d.url}`).join('\n');
  let resultado = texto
    .split('{primeiro_nome}').join(primeiroNome)
    .split('{nome}').join(valores.nome)
    .split('{remetente}').join(valores.remetente)
    .split('{codigo_rastreio}').join(valores.codigoRastreio ?? '')
    .split('{data_prevista}').join(valores.dataPrevista ?? 'a definir')
    .split('{endereco}').join(valores.endereco || SEM_ENDERECO);
  if (resultado.includes('{documentos}')) {
    resultado = blocoDocumentos
      ? resultado.split('{documentos}').join(blocoDocumentos)
      : resultado
          .split('\n')
          .filter((linha) => linha.trim() !== '{documentos}')
          .join('\n')
          .split('{documentos}')
          .join('');
  } else if (blocoDocumentos) {
    resultado = `${resultado}\n\n${blocoDocumentos}`;
  }
  return resultado;
}

/** Modelo criado pelo SI (além dos 7): não é escolhido sozinho — aparece como alternativa no
 * seletor "Mensagem" da Movimentação, filtrado pelo fluxo. */
export type FluxoModeloExtra = 'todos' | 'envio' | 'devolucao';

export const FLUXO_MODELO_EXTRA_LABELS: Record<FluxoModeloExtra, string> = {
  todos: 'Envio e devolução',
  envio: 'Só envio',
  devolucao: 'Só devolução',
};

export function modeloExtraVale(fluxo: FluxoModeloExtra, tipoFluxo: 'envio' | 'devolucao'): boolean {
  return fluxo === 'todos' || fluxo === tipoFluxo;
}
